import React from 'react';
import { Link } from 'react-router-dom';
import { Info } from 'lucide-react';
import ContainerPageLayout, { CodeBlock, ReqBadge, DownloadButtons } from '../components/ContainerPageLayout';
import ApiPlayground from '../components/ApiPlayground';

const TOC = [
  { id: 'overview', label: 'Overview' },
  { id: 'process', label: 'Refund Process' },
  { id: 'making-request', label: 'Making The Request', children: [
    { id: 'sample-request-url', label: 'Sample Request URL' },
    { id: 'sample-csharp', label: 'Sample Refund Request' },
    { id: 'request-reference', label: 'Request Reference' },
  ]},
  { id: 'available-form-fields', label: 'Available Form Fields' },
  { id: 'reason-codes', label: 'Reason Codes Reference' },
  { id: 'validation', label: 'Field Validation' },
  { id: 'sample-xml', label: 'Sample Refund XML' },
  { id: 'data-returned', label: 'Data Returned' },
  { id: 'expected-responses', label: 'Expected XML Documents' },
];

const REQUEST_URL_SAMPLE = `https://staging.ecomm365.com/acqapi/service.ashx?Username=SomeName&Password=SomePassword&MessageID=30dd879c-ee2f-11db-8314-0800200c9a66&APISignature=refund&Data=<R>...</R>`;

const CSHARP_SAMPLE = `[HttpPost]
public IActionResult index()
{
    HttpWebRequest httpWReq = (HttpWebRequest)WebRequest.Create("https://staging.ecomm365.com/acqapi/service.ashx"); //Create webrequest while setting the API URL to eComm staging environment
    UTF8Encoding encoding = new UTF8Encoding(); //Represents a UTF-8 encoding of Unicode characters.

    string pData = "username=SomeName"; //API Username
    pData += "&password=SomePassword"; //API Password
    pData += "&messageId=" + System.Guid.NewGuid().ToString();  //A new GUID is required for every new API Call
    pData += "&ApiSignature=Refund"; //API Signature - Please notice the \`Refund\` signature here for the Refund request

    pData += "&Data=" + WebUtility.UrlEncode(@"<R>...</R>"); //Data fields required for request

    byte[] data = encoding.GetBytes(pData); //Getting Bytes for data

    httpWReq.Method = "POST"; //HTTP Method - POST
    httpWReq.ContentType = "application/x-www-form-urlencoded"; //Setting the correct Content Type
    httpWReq.ContentLength = data.Length; //Getting Content Length

    byte[] byteArray = Encoding.UTF8.GetBytes(pData);
    Stream dataStream = httpWReq.GetRequestStream();
    dataStream.Write(byteArray, 0, byteArray.Length);
    dataStream.Close();

    HttpWebResponse response = (HttpWebResponse)httpWReq.GetResponse();
    string responseString = new StreamReader(response.GetResponseStream()).ReadToEnd();

    XmlDocument xDoc = new XmlDocument();
    xDoc.LoadXml(responseString);

    XmlNodeList ResponseR1 = xDoc.GetElementsByTagName("R1");
    XmlNodeList ResponseR2 = xDoc.GetElementsByTagName("R2");
    //... etc

    return View();
}`;

const REFUND_XML = `<!-- Refund Request Demonstration -->
<R>
    <R1>43DAAL4B-78CF-4000-AC6B-EBA1F1116229</R1>
    <R2>100</R2>
    <R3>1</R3>
    <R4>1000596</R4>
    <R5>http://domain.com/NotificationURL/</R5>
</R>`;

const RESP_0054 = `<!-- Sample Server Response, XML Document -->
<?xml version="1.0"?>
<R>
    <R1>0054</R1>
    <R2>Successful refund</R2>
</R>`;

const RESP_0023 = `<!-- Sample Server Response, XML Document -->
<?xml version="1.0"?>
<R>
    <R1>0023</R1>
    <R2>Refund received</R2>
</R>`;

const RESP_NOTIF = `<!-- Sample Confirmation Notification, XML Document -->
<?xml version="1.0"?>
<R>
  <R1>0000</R1>
  <R2>Successful</R2>
  <R3>43DAAL4B-78CF-4000-AC6B-EBA1F1116229</R3>
  <R4>Successful</R4>
  <R11>MerchantData</R11>
  <R12>MerchantReferenceNumber</R12>
</R>`;

const FORM_FIELDS = [
  { name: 'R1', desc: 'Transaction Code — the Transaction Code used in the Sale request.', req: 'Y', def: 'AN(40)' },
  { name: 'R2', desc: 'Amount To Refund (without decimals). E.g. €100.00 = [10000], €123.67 = [12367], €0.99 = [99].', req: 'Y', def: 'N(20)' },
  { name: 'R3', desc: 'Reason for refund — single digit code (see Reason Codes Reference below).', req: 'Y', def: 'AN(50)' },
  { name: 'R4', desc: 'Vendor ID.', req: 'Y', def: 'AN(50)' },
  { name: 'R5', desc: 'Notification URL (where the confirmation notification will be sent).', req: 'Y', def: 'AN(2000)' },
];

const VALIDATION = [
  { name: 'R1', desc: 'Transaction Code.', regex: '^[-_0-9A-Za-z]{0,40}$' },
  { name: 'R2', desc: 'Amount To Refund.', regex: '^[0-9]{0,20}$' },
  { name: 'R3', desc: 'Reason For Refund.', regex: 'From list of reasons' },
  { name: 'R4', desc: 'Vendor ID.', regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R5', desc: 'Notification URL.', regex: 'https?:\\/\\/?[-a-zA-Z0-9@:%._\\+\\~#=]{2,256}\\b(...)' },
];

const RESP_FIELDS = [
  { name: 'R1', desc: (<>Error code (see <Link to="/technical-documentation/appendix/error-codes" className="text-[#1F4E79] underline hover:text-[#163a5f] font-semibold">Error Codes</Link>).</>), def: 'N(4)' },
  { name: 'R2', desc: 'Description.', def: 'AN(250)' },
];

const Refund = () => (
  <ContainerPageLayout
    currentId="refund"
    title="Refund"
    subtitle="Refunds a previously settled transaction — partial or full — and sends a confirmation notification to your Notification URL."
    signature="refund"
    toc={TOC}
    mainTestId="refund-page"
    sidebarTestId="refund-sidebar"
  >
    {/* Overview */}
    <section id="overview" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Overview</h2>
      <p className="text-gray-700 text-base leading-relaxed">
        The <strong>Refund</strong> API is only required if the merchant wants to refund a transaction. The Refund API is the most commonly used and is a must-have if you intend on building an administration panel.
      </p>

      <div className="bg-blue-50/80 border border-blue-200 rounded-lg p-4 mt-5 flex items-start gap-3">
        <Info size={18} className="text-[#1F4E79] flex-shrink-0 mt-0.5" />
        <p className="text-sm text-gray-700">
          <strong>Partial refunds supported:</strong> You can refund any amount up to — but not exceeding — the total of the original Sale. If the transaction is still frozen in a Pre-Auth state, use{' '}
          <Link to="/technical-documentation/api-reference/void-pre-auth" className="text-[#1F4E79] underline hover:text-[#163a5f]">Void Pre-Auth</Link> instead.
        </p>
      </div>
    </section>

    {/* Process */}
    <section id="process" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Refund Process</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        The process is simple and straightforward. Below demonstrates the steps required to successfully make the request.
      </p>
      <ol className="list-decimal pl-6 space-y-3 text-gray-700 text-base leading-relaxed">
        <li>The transaction information is submitted to our API.</li>
        <li>
          The merchant will receive a response of <strong className="text-[#c32222]">"0023 — Refund Received"</strong> if the notification method is set to <em>Notification</em>; a confirmation notification will be sent to the merchant's Notification URL with the transaction result.
          <br className="hidden sm:block" />
          The merchant will receive a response of <strong className="text-[#c32222]">"0054 — Successful Refund"</strong> if the notification method is set to <em>Both</em> or <em>Server</em>, indicating a successful refund; you will also receive a confirmation notification. The confirmation notification will contain the response <strong className="text-[#c32222]">"0000 — Successful"</strong> indicating that the transaction in question is now refunded. Any other response indicates a failed refund.
        </li>
      </ol>
    </section>

    {/* Making The Request */}
    <section id="making-request" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Making The Request</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-3">The request string for the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">Refund</code> call must be composed of the following information:</p>
      <ol className="list-decimal pl-6 space-y-1 text-gray-700 text-sm mb-6">
        <li><strong>Username</strong> = SomeName</li>
        <li><strong>Password</strong> = SomePassword</li>
        <li><strong>MessageID</strong> = <em>*GUID (e.g. 30dd879c-ee2f-11db-8314-0800200c9a66)</em></li>
        <li><strong>APISignature</strong> = Refund</li>
        <li><strong>Data</strong> = Form data in XML format</li>
      </ol>

      <div id="sample-request-url" className="scroll-mt-28">
        <h3 className="text-lg font-bold text-gray-900 mb-2">A fully formatted HTTP POST request should look like this:</h3>
        <CodeBlock code={REQUEST_URL_SAMPLE} lang="http" testid="request-url-code" />
      </div>

      <div id="sample-csharp" className="scroll-mt-28 mt-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h3 className="text-xl font-bold text-gray-900">Sample Refund Request</h3>
          <DownloadButtons />
        </div>
        <CodeBlock code={CSHARP_SAMPLE} lang="csharp" testid="sample-csharp-code" />
      </div>

      <div id="request-reference" className="scroll-mt-28 mt-10">
        <ApiPlayground
          apiSignature="Refund"
          title="Request Reference — Refund"
          description="Refund a previously settled transaction."
          defaultData={REFUND_XML.replace('<!-- Refund Request Demonstration -->\n', '')}
        />
      </div>
    </section>

    {/* Form Fields */}
    <section id="available-form-fields" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Available Form Data Fields</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        Below is a table containing all the available fields for passing POST data into the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">data</code> parameter within the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">Refund</code> request. The value you enter for the <strong>Amount</strong> field must not exceed the total used in the sale request as the request will fail.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse" data-testid="form-fields-table">
          <thead>
            <tr className="bg-[#1F4E79] text-white">
              <th className="text-left p-3 w-20">Field</th>
              <th className="text-left p-3">Description</th>
              <th className="text-left p-3 w-24">Required</th>
              <th className="text-left p-3 w-28">Definition</th>
            </tr>
          </thead>
          <tbody>
            {FORM_FIELDS.map((f, i) => (
              <tr key={f.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                <td className="p-3 font-mono font-semibold text-[#1F4E79]">{f.name}</td>
                <td className="p-3">{f.desc}</td>
                <td className="p-3"><ReqBadge value={f.req} /></td>
                <td className="p-3 font-mono text-xs">{f.def}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>

    {/* Reason Codes */}
    <section id="reason-codes" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Reason Codes Reference</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-6">
        Supply a single digit into the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">&lt;R3&gt;</code> field. The accepted codes depend on the card scheme of the original Sale transaction:
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="rounded-lg border-2 border-[#1A1F71]/20 bg-gradient-to-br from-white to-[#1A1F71]/5 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="px-3 py-1 bg-[#1A1F71] text-white text-xs font-bold rounded uppercase tracking-wide">Visa</div>
            <span className="text-sm text-gray-600">3 reason codes</span>
          </div>
          <ul className="text-sm text-gray-700 space-y-2">
            <li className="flex gap-3">
              <code className="flex-shrink-0 w-6 h-6 bg-[#1A1F71] text-white rounded font-mono font-bold text-center leading-6">1</code>
              <span>Transaction Voided By Customer</span>
            </li>
            <li className="flex gap-3">
              <code className="flex-shrink-0 w-6 h-6 bg-[#1A1F71] text-white rounded font-mono font-bold text-center leading-6">2</code>
              <span>Transaction Not Completed</span>
            </li>
            <li className="flex gap-3">
              <code className="flex-shrink-0 w-6 h-6 bg-[#1A1F71] text-white rounded font-mono font-bold text-center leading-6">3</code>
              <span>No Confirmation From Point Of Service</span>
            </li>
          </ul>
        </div>

        <div className="rounded-lg border-2 border-[#EB001B]/20 bg-gradient-to-br from-white to-[#FF5F00]/5 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="px-3 py-1 bg-gradient-to-r from-[#EB001B] to-[#FF5F00] text-white text-xs font-bold rounded uppercase tracking-wide">Mastercard</div>
            <span className="text-sm text-gray-600">4 reason codes</span>
          </div>
          <ul className="text-sm text-gray-700 space-y-2">
            <li className="flex gap-3">
              <code className="flex-shrink-0 w-6 h-6 bg-[#EB001B] text-white rounded font-mono font-bold text-center leading-6">1</code>
              <span>Customer Cancellation</span>
            </li>
            <li className="flex gap-3">
              <code className="flex-shrink-0 w-6 h-6 bg-[#EB001B] text-white rounded font-mono font-bold text-center leading-6">2</code>
              <span>Error</span>
            </li>
            <li className="flex gap-3">
              <code className="flex-shrink-0 w-6 h-6 bg-[#EB001B] text-white rounded font-mono font-bold text-center leading-6">3</code>
              <span>Response Received Late</span>
            </li>
            <li className="flex gap-3">
              <code className="flex-shrink-0 w-6 h-6 bg-[#EB001B] text-white rounded font-mono font-bold text-center leading-6">4</code>
              <span>Suspect Fraud</span>
            </li>
          </ul>
        </div>
      </div>
    </section>

    {/* Validation */}
    <section id="validation" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Available Form Data Fields — Validation</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        Below is a table containing all the available fields for the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">data</code> parameter within the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">Refund</code> request including its validation. These are used when constructing the data.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-[#1F4E79] text-white">
              <th className="text-left p-3 w-20">Field</th>
              <th className="text-left p-3">Description</th>
              <th className="text-left p-3">Validation (Regex)</th>
            </tr>
          </thead>
          <tbody>
            {VALIDATION.map((v, i) => (
              <tr key={v.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                <td className="p-3 font-mono font-semibold text-[#1F4E79]">{v.name}</td>
                <td className="p-3">{v.desc}</td>
                <td className="p-3 font-mono text-xs break-all">{v.regex}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>

    {/* Sample XML */}
    <section id="sample-xml" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Sample <code className="bg-gray-100 px-2 py-0.5 rounded text-2xl font-mono">Refund</code> XML Document</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        The below sample demonstrates what is expected when passing POST data into the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">data</code> parameter.
      </p>
      <CodeBlock code={REFUND_XML} lang="xml" testid="refund-xml-code" />
    </section>

    {/* Data Returned */}
    <section id="data-returned" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Data Returned</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">Below are the expected XML fields returned from the request.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-[#1F4E79] text-white">
              <th className="text-left p-3 w-20">Field</th>
              <th className="text-left p-3">Description</th>
              <th className="text-left p-3 w-28">Definition</th>
            </tr>
          </thead>
          <tbody>
            {RESP_FIELDS.map((f, i) => (
              <tr key={f.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                <td className="p-3 font-mono font-semibold text-[#1F4E79]">{f.name}</td>
                <td className="p-3">{f.desc}</td>
                <td className="p-3 font-mono text-xs">{f.def}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>

    {/* Expected Responses */}
    <section id="expected-responses" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Expected XML Documents</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        This section demonstrates the expected XML document responses returned from the HTTP POST once the request has passed validation.
      </p>

      <div className="bg-blue-50/80 border border-blue-200 rounded-lg p-5 mb-6">
        <p className="font-semibold text-[#1F4E79] mb-3">Expected error code responses:</p>
        <ol className="list-decimal pl-5 text-sm text-gray-700 space-y-2">
          <li><strong className="text-[#c32222]">0054</strong> — Indicates that the notification method is set to "Notification". The confirmation notification will be sent to the Notification URL.</li>
          <li><strong className="text-[#c32222]">0023</strong> — Indicates a successful refund; you will also receive a confirmation notification to the Notification URL.</li>
        </ol>
      </div>

      <h3 className="text-lg font-bold text-gray-900 mb-2">Sample XML Response (0054)</h3>
      <CodeBlock code={RESP_0054} lang="xml" testid="resp-0054-code" />

      <h3 className="text-lg font-bold text-gray-900 mb-2 mt-6">Sample XML Response (0023)</h3>
      <CodeBlock code={RESP_0023} lang="xml" testid="resp-0023-code" />

      <h3 className="text-lg font-bold text-gray-900 mb-2 mt-6">Sample XML Response (Confirmation Notification)</h3>
      <p className="text-sm text-gray-600 mb-2">The below indicates a successful refund notification, expected to return to the Notification URL.</p>
      <CodeBlock code={RESP_NOTIF} lang="xml" testid="resp-notif-code" />
    </section>
  </ContainerPageLayout>
);

export default Refund;
